import React, { Component } from 'react';

/**
 * The takeover function is a HOC[0] for sharing takeover behaviors amongst our
 * code base. If you only pass a component, then it will takeover the body on mount
 * and return the body to its original state on unmount.
 *
 * If you pass a takeover prop, it will only take over the body when that prop
 * reads true and return the body when that prop changes to false.
 *
 * @param {Component} WrappedComponent - The component that will be taking over
 * @param {string} [takeoverProp] - The name of the prop that will signal a takeover
 * @return {Component}
 *
 * 0: https://facebook.github.io/react/docs/higher-order-components.html
 */

export default function takeover(WrappedComponent, takeoverProp) {
  class TakeOver extends Component {
    constructor(props) {
      super(props);
      this.state = {
        isOpen: false,
        scrollY: 0,
      };
    }

    componentDidMount() {
      if (!takeoverProp || this.props[takeoverProp] === true) {
        return this.updateBodyStyles(true);
      }
    }

    componentWillUnmount() {
      return this.updateBodyStyles(false);
    }

    static getDerivedStateFromProps(nextProps, prevState) {
      const { isOpen = false } = nextProps;
      const { scrollY = 0 } = prevState;
      let updatedState = { isOpen, scrollY };

      // If we're transitioning from closed to open, let's store the scrollY...
      if (
        !prevState.isOpen &&
        (!takeoverProp || nextProps[takeoverProp] === true)
      ) {
        updatedState = {
          isOpen: true,
          scrollY: typeof window !== 'undefined' ? window.scrollY : 0,
        };
      }

      return updatedState;
    }

    componentDidUpdate(prevProps) {
      if (!takeoverProp || typeof takeoverProp !== 'string') return;

      if (
        typeof prevProps !== 'undefined' &&
        prevProps[takeoverProp] === this.props[takeoverProp]
      )
        return;

      if (this.props[takeoverProp]) {
        this.updateBodyStyles(true);
      } else {
        this.updateBodyStyles(false);
      }
    }

    updateBodyStyles = (activate = false) => {
      if (document) {
        if (activate) {
          document.body.style.overflow = 'hidden';
          document.body.style.position = 'fixed';
          document.body.style.width = '100%';
          document.body.style.top = `-${this.state.scrollY}px`;
        } else {
          document.body.style.overflow = '';
          document.body.style.position = '';
          document.body.style.width = '';
          document.body.style.top = '';
          window.scrollTo(0, this.state.scrollY);
        }
      }
    };

    render() {
      return <WrappedComponent {...this.props} />;
    }
  }

  TakeOver.displayName = `TakeOver(${getDisplayName(WrappedComponent)})`;

  return TakeOver;
}

function getDisplayName(WrappedComponent) {
  return WrappedComponent.displayName || WrappedComponent.name || 'Component';
}
